import React from 'react';
import { 
  getQuizScoreBgColorClass, 
  getQuizScoreTextColorClass,
  getCheatingProbabilityBgColorClass,
  getCheatingProbabilityTextColorClass,
  getCheatingProbabilityLabel
} from '../../Utils/ColorUtils';

const QuizSummary = ({ practicalQuizResponse, quizDuration, cheatingData, onViewResults, layout = "columns" }) => {
  // Format duration for display in compact format (e.g., "5h 23m")
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  };
  
  // Helper function to extract numeric value from cheating probability
  const extractProbabilityValue = (probability) => {
    if (!probability && probability !== 0) return 0;
    // Convert to string first to handle both string and number inputs
    const probabilityString = String(probability);
    return parseInt(probabilityString.replace('%', ''));
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-xl font-semibold text-gray-800">Practical Quiz Summary</h4>
        {onViewResults && (
          <button 
            onClick={onViewResults}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Results
          </button>
        )}
      </div>
      
      <div className={`grid grid-cols-1 ${layout === "columns" ? "md:grid-cols-2" : ""} gap-6`}>
        {/* Practical Quiz Score Column */}
        <div className="border border-gray-200 rounded-lg p-4">
          {practicalQuizResponse && practicalQuizResponse.totalScore !== undefined ? (
            <div>
              <div className="flex flex-col md:flex-row">
                {/* Score Display - Left Side */}
                <div className="md:w-1/3 flex flex-col items-start mb-4 md:mb-0">
                  <div className="w-full max-w-[200px]">
                    <div className={`text-center p-4 rounded-lg ${getQuizScoreBgColorClass(practicalQuizResponse.totalScore)}`}>
                      <p className={`text-5xl font-bold ${getQuizScoreTextColorClass(practicalQuizResponse.totalScore)}`}>
                        {practicalQuizResponse.totalScore}%
                      </p>
                    </div>
                    
                    {quizDuration !== undefined && (
                      <div className="mt-2 text-center p-4 rounded-lg bg-gray-200">
                        <div className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium text-gray-700">Done in {formatDuration(quizDuration)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Score Rationale - Right Side */}
                <div className="md:w-2/3 md:pl-4">
                  <h5 className="text-lg font-medium text-gray-800 mb-2">Practical Quiz Score</h5>
                  {practicalQuizResponse.scoreRationale ? (
                    <p className="text-gray-600">{practicalQuizResponse.scoreRationale}</p>
                  ) : (
                    <p className="text-gray-500 italic">No score rationale available.</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">No practical quiz data available.</p>
          )}
        </div>
        
        {/* Cheating Probability Column */}
        <div className="border border-gray-200 rounded-lg p-4">
          {cheatingData ? (
            <div className="flex flex-col">
              <div className="flex flex-col md:flex-row">
                {/* Cheating Score Display - Left Side */}
                <div className="md:w-1/3 flex flex-col items-start mb-4 md:mb-0">
                  <div className="w-full max-w-[200px]">
                    {cheatingData.cheatingProbability && (
                      <>
                        <div className={`text-center p-4 rounded-lg ${getCheatingProbabilityBgColorClass(extractProbabilityValue(cheatingData.cheatingProbability))}`}>
                          <p className={`text-5xl font-bold ${getCheatingProbabilityTextColorClass(extractProbabilityValue(cheatingData.cheatingProbability))}`}>
                            {getCheatingProbabilityLabel(extractProbabilityValue(cheatingData.cheatingProbability))}
                          </p>
                        </div>
                        <div className="mt-2 text-center p-4 rounded-lg bg-gray-200">
                          <div className="flex items-center justify-center">
                            <span className="font-medium text-gray-700">Percent: {cheatingData.cheatingProbability}</span>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {cheatingData.eventNumber !== undefined && (
                      <div className="mt-2 text-center p-4 rounded-lg bg-gray-200">
                        <div className="flex items-center justify-center">
                          <span className="font-medium text-gray-700">Events: {String(cheatingData.eventNumber)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Integrity Risk Score - Right Side */}
                <div className="md:w-2/3 md:pl-4">
                  <h5 className="text-lg font-medium text-gray-800 mb-2">Integrity Risk Score</h5>
                  {cheatingData.rationale && cheatingData.rationale.length > 0 ? (
                    <ul className="list-disc pl-5 space-y-1 text-gray-600">
                      {cheatingData.rationale.map((reason, index) => (
                        <li key={index}>{reason}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 italic">No integrity assessment available.</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">No integrity data available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizSummary;
